import { isCategoricalAxis } from '../types/guards.js';
import type {
  RectDatapointInternal,
  XYChartAxisInternal,
  XYChartSeriesWithDatapoints,
} from '../types/xy-chart-internals.js';
import type {
  AreaDatapoint,
  BarDatapoint,
  DotDatapoint,
  LineDatapoint,
} from '../types/xy-chart.js';

/**
 * Counts the total datapoints, series, and categories in a selection.
 *
 * @param selectedDatapoints - Array of selected datapoints per series
 * @param seriesWithDatapoints - Array of series with their datapoints
 * @param axes - Array of chart axes
 * @returns Object containing total counts for datapoints, series, and categories
 */
export const countDatapointsWithSeriesAndCategories = (
  selectedDatapoints: (
    | DotDatapoint[]
    | RectDatapointInternal[]
    | LineDatapoint[]
    | BarDatapoint[]
    | AreaDatapoint[]
    | undefined
  )[],
  seriesWithDatapoints: XYChartSeriesWithDatapoints[],
  axes: XYChartAxisInternal[],
) => {
  let totalCount = 0;
  let totalSeries = 0;
  const axesCategories = new Set<string>();

  selectedDatapoints.forEach((datapointArray, seriesIndex) => {
    if (!datapointArray?.length) {
      return;
    }

    const series = seriesWithDatapoints[seriesIndex];
    if (series) {
      const xAxis = axes.find((axis) => axis.id === series.xAxisId);

      totalCount += datapointArray.length;

      if (xAxis && isCategoricalAxis(xAxis)) {
        datapointArray.forEach((datapoint) => {
          const xValue = datapoint.x0 ?? datapoint.x1;
          axesCategories.add(`${xAxis.id}_${xValue}`);
        });
      } else {
        totalSeries += 1;
      }
    }
  });

  return {
    selectedDatapoints: totalCount,
    totalSeries,
    totalCategories: axesCategories.size,
  };
};
